/**
 * Shared helpers for calendar event formatting, time calculation, and display.
 */
import moment from 'moment-timezone';

// ---------------------------------------------------------------------------
// Time formatting
// ---------------------------------------------------------------------------

/** Format start+end ISO to "2:30 PM - 3:00 PM" in the given timezone. */
export function formatEventTime(
  start: string,
  end: string,
  timezone?: string
): string {
  const tz = timezone || moment.tz.guess();
  const s = moment.tz(start, tz);
  const e = moment.tz(end, tz);
  if (!s.isValid() || !e.isValid()) return '';
  return `${s.format('h:mm A')} - ${e.format('h:mm A')}`;
}

/** Format a single ISO to "2:30 PM" in the given timezone. */
export function formatTime(iso: string, timezone?: string): string {
  const tz = timezone || moment.tz.guess();
  const m = moment.tz(iso, tz);
  return m.isValid() ? m.format('h:mm A') : '';
}

// ---------------------------------------------------------------------------
// Duration / time math
// ---------------------------------------------------------------------------

/** Calculate duration in minutes between two ISO strings. */
export function calcDurationMinutes(start: string, end: string): number {
  const s = moment(start);
  const e = moment(end);
  if (!s.isValid() || !e.isValid()) return 0;
  return Math.round(e.diff(s, 'minutes'));
}

/** Check if a datetime is in the past relative to the given timezone. */
export function isInPast(iso: string, timezone?: string): boolean {
  const tz = timezone || moment.tz.guess();
  return moment.tz(iso, tz).isBefore(moment().tz(tz));
}

/** Get the end ISO string from start + duration. */
export function addDuration(
  startIso: string,
  durationMinutes: number,
  timezone?: string
): string {
  const tz = timezone || moment.tz.guess();
  const m = moment.tz(startIso, tz);
  if (!m.isValid()) return '';
  return m.add(durationMinutes, 'minutes').toISOString();
}

// ---------------------------------------------------------------------------
// Event display helpers
// ---------------------------------------------------------------------------

/** Dynamic padding based on slot duration — tighter for short events. */
export function getEventPadding(durationMinutes: number): string {
  if (durationMinutes <= 15) return '0px 2px';
  if (durationMinutes <= 30) return '4px';
  return '6px';
}

/** Max duration in minutes (24 hours — matches form field cap). */
export const MAX_DURATION_MINUTES = 1440;

/**
 * For a click on a busy-event harness, return the time range the harness
 * actually covers. FullCalendar renders one harness per day for a
 * multi-day event, but exposes `event.startStr` / `event.endStr` of the
 * full event (across all days). Mapping a click using the full event
 * range against the per-day harness rect produces wrong times — the
 * proportion-of-rect maps to a different proportion-of-event.
 *
 * Walks up to the day column (`[data-date]`), clamps the full event
 * range to that day's 00:00–24:00 window (matches our calendars'
 * `slotMinTime`/`slotMaxTime` config), and returns the chunk bounds.
 * Single-day events: clamp is a no-op, returns full bounds unchanged.
 */
export function busyEventChunkBounds(
  el: HTMLElement,
  fullStartMs: number,
  fullEndMs: number,
  timezone: string
): { chunkStartMs: number; chunkEndMs: number } {
  const dayCol = el.closest<HTMLElement>('[data-date]');
  const dateStr = dayCol?.getAttribute('data-date');
  if (!dateStr) return { chunkStartMs: fullStartMs, chunkEndMs: fullEndMs };
  const dayStart = moment.tz(dateStr, 'YYYY-MM-DD', timezone).valueOf();
  const dayEnd = moment
    .tz(dateStr, 'YYYY-MM-DD', timezone)
    .add(1, 'day')
    .valueOf();
  return {
    chunkStartMs: Math.max(fullStartMs, dayStart),
    chunkEndMs: Math.min(fullEndMs, dayEnd),
  };
}

/** Drag threshold in pixels to distinguish click from drag. */
export const DRAG_THRESHOLD_PX = 5;

/** Default slot duration in minutes (one grid cell). */
export const SLOT_DURATION_MINUTES = 15;

// ---------------------------------------------------------------------------
// Week range helpers
// ---------------------------------------------------------------------------

/** Get the week range key for a FullCalendar datesSet arg. */
export function getWeekRangeKey(startStr: string, endStr: string): string {
  return `${startStr}__${endStr}`;
}

/** Get a scroll-time string (HH:MM:SS) for a given hour, offset by -2. */
export function getScrollTime(hour: number): string {
  const scrollHour = Math.max(0, Math.min(22, hour - 2));
  return `${String(scrollHour).padStart(2, '0')}:00:00`;
}

/** Get the scroll time for "now" offset by -2 hours. */
export function getScrollTimeForNow(timezone?: string): string {
  const tz = timezone || moment.tz.guess();
  const hour = moment().tz(tz).hour();
  return getScrollTime(hour);
}
